import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { Zap, DollarSign, Leaf, Battery, TreePine, Calendar, Sun } from "lucide-react";

/**
 * <ProductionDashboard projectId /> — Tesla / Enphase style.
 *
 * Daily curve from /api/projects/{id}/production/today
 * Lifetime + 30d bars from /api/projects/{id}/production/summary
 *
 * Telemetry is currently mocked server-side (deterministic per project+date).
 * Wiring real Enphase Envoy / SolarEdge data swaps the mock for live values
 * without changing this component.
 */
export default function ProductionDashboard({ projectId }) {
  const { t } = useTranslation();
  const [today, setToday] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notInstalled, setNotInstalled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t1, s1] = await Promise.all([
          api.get(`/projects/${projectId}/production/today`),
          api.get(`/projects/${projectId}/production/summary`),
        ]);
        if (!cancelled) {
          setToday(t1.data);
          setSummary(s1.data);
        }
      } catch (err) {
        if (err?.response?.status === 409) setNotInstalled(true);
        else console.warn("production fetch:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) return <div className="text-sm text-[#475569] py-10 text-center" data-testid="production-loading">{t("common.loading")}</div>;

  if (notInstalled) {
    return (
      <div className="card-pb p-8 text-center" data-testid="production-not-installed">
        <Sun className="w-10 h-10 mx-auto text-[#E2E8F0] mb-3" />
        <div className="text-[#0A2540] font-medium">{t("production.notInstalledYet")}</div>
      </div>
    );
  }

  if (!today || !summary) return null;

  return (
    <div data-testid="production-dashboard" className="space-y-5">
      {/* Hero today */}
      <div className="card-pb p-6 bg-gradient-to-br from-[#0A2540] to-[#1E3A66] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A356]/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[#C9A356] text-xs uppercase tracking-widest mb-2">
            <Sun className="w-3.5 h-3.5" /> {t("production.now")}
          </div>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="font-heading text-5xl font-bold tabular-nums" data-testid="prod-current-kw">{today.current_kw.toFixed(1)}</span>
            <span className="text-xl text-white/70">kW</span>
          </div>
          <div className="text-sm text-white/70">{today.weather}</div>

          <div className="grid grid-cols-3 gap-3 mt-5">
            <MiniTile icon={Zap} label={t("production.todayProduction")} value={`${today.today_kwh} kWh`} testId="prod-today-kwh" />
            <MiniTile icon={DollarSign} label={t("production.savedToday")} value={`$${today.estimated_savings_usd.toFixed(2)}`} testId="prod-today-saved" />
            <MiniTile icon={Leaf} label={t("production.co2Avoided")} value={`${today.co2_avoided_kg} kg`} testId="prod-today-co2" />
          </div>
        </div>
      </div>

      {/* Hourly curve */}
      <div className="card-pb p-5">
        <div className="overline mb-3 flex items-center gap-2">
          <Sun className="w-3.5 h-3.5 text-[#C9A356]" /> {t("production.today")}
        </div>
        <div style={{ width: "100%", height: 180, minHeight: 180 }}>
          <ResponsiveContainer>
            <AreaChart data={today.curve} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <defs>
                <linearGradient id="prod-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9A356" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#C9A356" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 3" stroke="#E2E8F0" />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(h) => `${h % 12 || 12}${h < 12 ? "a" : "p"}`} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} unit=" kW" />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }}
                       formatter={(v) => [`${v} kW`, "Output"]}
                       labelFormatter={(h) => `${h}:00`} />
              <Area type="monotone" dataKey="kw" stroke="#C9A356" strokeWidth={2} fill="url(#prod-area)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Battery */}
      {today.battery?.capacity_kwh > 0 && (
        <div className="card-pb p-5" data-testid="prod-battery">
          <div className="flex items-center justify-between mb-3">
            <div className="overline flex items-center gap-2">
              <Battery className="w-3.5 h-3.5 text-[#C9A356]" /> {t("production.battery")}
            </div>
            <div className="text-xs text-[#475569]">
              {today.battery.flow_w >= 0 ? t("production.charging") : t("production.discharging")} · {Math.abs(today.battery.flow_w)} W
            </div>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                 style={{ width: `${today.battery.state_of_charge_pct}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs text-[#475569]">
            <span>{today.battery.state_of_charge_pct}%</span>
            <span>{today.battery.capacity_kwh} kWh capacity</span>
          </div>
        </div>
      )}

      {/* Lifetime */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiTile icon={Zap}        label={t("production.lifetime")}    value={`${summary.lifetime_kwh.toLocaleString()} kWh`} sub={`${summary.days_since_install} days`} testId="prod-lifetime-kwh" />
        <KpiTile icon={DollarSign} label={t("production.lifetime")}    value={`$${summary.lifetime_savings_usd.toLocaleString()}`} sub="USD saved" testId="prod-lifetime-usd" />
        <KpiTile icon={Calendar}   label={t("production.thisMonth")}   value={`${summary.this_month_kwh.toLocaleString()} kWh`} sub={`$${summary.month_savings_usd}`} testId="prod-month-kwh" />
        <KpiTile icon={TreePine}   label={t("production.treesEquivalent")} value={`${summary.trees_equivalent}`} sub={`${summary.lifetime_co2_kg.toLocaleString()} kg CO₂`} testId="prod-trees" />
      </div>

      {/* 30-day bars */}
      <div className="card-pb p-5">
        <div className="overline mb-3 flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-[#C9A356]" /> Last 30 days
        </div>
        <div style={{ width: "100%", height: 160, minHeight: 160 }}>
          <ResponsiveContainer>
            <BarChart data={summary.last_30_days} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="2 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94A3B8" }} tickFormatter={(d) => d.slice(8, 10)} interval={2} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} unit=" kWh" />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }}
                       formatter={(v) => [`${v} kWh`, "Daily output"]} />
              <Bar dataKey="kwh" fill="#C9A356" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {today.telemetry_source === "mock" && (
        <p className="text-[10px] text-[#94A3B8] text-center">
          Estimated production · Real inverter telemetry coming soon
        </p>
      )}
    </div>
  );
}

function MiniTile({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3" data-testid={testId}>
      <div className="flex items-center gap-1 text-[#C9A356] text-[10px] uppercase tracking-widest mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-heading text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="card-pb p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#475569]">
        <Icon className="w-4 h-4 text-[#C9A356]" />
        <span className="overline">{label}</span>
      </div>
      <div className="font-heading text-xl font-bold text-[#0A2540] dark:text-white mt-1 tabular-nums">{value}</div>
      <div className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</div>
    </div>
  );
}
